import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReservationForm from '../components/ReservationForm';
import { getRestaurantById } from '../services/restaurantService';
import { getEventById } from '../services/eventService';
import { normalizeNoteHtml } from '../utils/richText';
import './ReservationWidget.css';

/**
 * ExperiencePage — dedicated full-page booking page for a single experience.
 *
 * This is where guests land when an experience has "Book from Widget" turned OFF
 * in the admin: instead of booking inside the embedded widget, its banner links
 * here (see ReservationForm.renderBanner). It works like the standalone KIKI'S
 * event page — an event hero on top, then the proven reservation engine in
 * `singleEvent` mode (date picker hidden, locked to this one experience) so the
 * booking behaves EXACTLY like the tested widget and never drifts from it.
 *
 * Route: /experience/:id  (id = event id)
 */

// "21:00 - 23:00" (24h) → "9:00 PM – 11:00 PM" (12h), to match the KIKI'S page.
const fmtOneTime = (t) => {
  if (!t) return '';
  const [h, m] = t.trim().split(':');
  const hr = parseInt(h, 10);
  if (Number.isNaN(hr)) return t;
  const ampm = hr >= 12 ? 'PM' : 'AM';
  const h12 = hr % 12 || 12;
  return `${h12}:${m ?? '00'} ${ampm}`;
};

const formatEventTime = (event) => {
  if (event.startTime) {
    const start = fmtOneTime(event.startTime);
    const end = event.endTime ? fmtOneTime(event.endTime) : '';
    return end ? `${start} – ${end}` : start;
  }
  return event.time || '';
};

const ExperiencePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  // Bumped to remount the booking form fresh after "Make Another Booking" so the
  // hero + house-rules/notes come back exactly like a page refresh (in-place).
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    if (!id) {
      setError('Experience not found.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const res = await getEventById(id);
        if (cancelled) return;
        if (res.accessDenied) {
          setAccessDenied(res.message || "This is a private experience. You don't have access.");
          setLoading(false);
          return;
        }
        if (!res.event) {
          setError('This experience could not be found.');
          setLoading(false);
          return;
        }
        setEvent(res.event);
        // Fetch the restaurant for its widget colors + name. Best-effort: fall
        // back to the relation embedded in the event if the call fails.
        try {
          const r = await getRestaurantById(res.event.restaurantId);
          if (!cancelled) setRestaurant(r);
        } catch {
          if (!cancelled) setRestaurant(res.event.restaurant || null);
        }
        setError(null);
      } catch (err) {
        console.error('Error loading experience:', err);
        if (!cancelled) setError('Something went wrong loading this experience.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="widget-loading-container">
        <div className="widget-spinner"></div>
        <p>Loading…</p>
      </div>
    );
  }

  if (accessDenied) {
    const isLoggedIn = !!localStorage.getItem('tali_client_token');
    return (
      <div className="widget-error-container" style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem', color: '#6f42c1' }}>
          <i className="bi bi-lock-fill"></i>
        </div>
        <h3 style={{ marginBottom: '0.75rem' }}>Private Experience</h3>
        <p className="widget-error-message" style={{ maxWidth: 480, margin: '0 auto 1rem' }}>
          {accessDenied}
        </p>
        {!isLoggedIn && (
          <a
            href={`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`}
            className="btn btn-primary"
            style={{ marginTop: '0.5rem' }}
          >
            Sign in to check access
          </a>
        )}
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="widget-error-container">
        <p className="widget-error-message">{error || 'Experience not found'}</p>
      </div>
    );
  }

  // Theme the page with the restaurant's own widget colors (same mapping as the
  // embedded widget) so the booking form looks identical to the tested widget.
  const customColorStyles = restaurant
    ? {
        '--primary-purple': restaurant.widgetPrimaryColor || '#1A1A1A',
        '--secondary-purple': restaurant.widgetSecondaryColor || '#2D2D2D',
        '--accent-lime': restaurant.widgetAccentColor || '#BFFF00',
        '--text-dark': restaurant.widgetTextColor || '#1a1a1a',
        '--white': restaurant.widgetBackgroundColor || '#ffffff',
        '--light-purple': restaurant.widgetSecondaryColor || '#333333',
        '--bg-light': restaurant.widgetBackgroundColor || '#f9f9f9',
      }
    : undefined;

  const heroImg = event.imageUrl || event.mobileCoverImageUrl;
  const dateText = event.dateFormatted || event.date || '';
  const timeText = formatEventTime(event);
  const houseRules = event.houseRules;

  return (
    <div className="reservation-widget-wrapper experience-page-wrapper" style={customColorStyles}>
      {/* Single unified card: hero flush at the top, the booking flow inside —
          the same layout as the standalone KIKI'S experience page. */}
      <div className="experience-page-card">
        {/* Event hero — experience image with the title overlaid. */}
        {!showSuccess && (
          <div className={`experience-page-hero${heroImg ? '' : ' no-image'}`}>
            {heroImg && (
              <img
                src={heroImg}
                alt={event.title || event.name}
                className="experience-page-hero-img"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <div className="experience-page-hero-overlay">
              <span className="experience-page-hero-badge">
                <i className="bi bi-ticket-perforated me-1"></i>Experience
              </span>
              <h1 className="experience-page-hero-title">{event.title || event.name}</h1>
            </div>
          </div>
        )}

        {/* Meta strip under the hero — always present (date/time/venue), so the
            page reads cleanly even when the experience has no house-rules note. */}
        {!showSuccess && (dateText || timeText || restaurant?.name) && (
          <div className="experience-page-meta">
            {dateText && (
              <span><i className="bi bi-calendar-event"></i>{dateText}</span>
            )}
            {timeText && (
              <span><i className="bi bi-clock"></i>{timeText}</span>
            )}
            {restaurant?.name && (
              <span><i className="bi bi-geo-alt"></i>{restaurant.name}</span>
            )}
          </div>
        )}

        <div className="experience-page-body">
          {/* House Rules (admin "Widget Top Note") — shown above the form. */}
          {houseRules && houseRules.replace(/<[^>]*>/g, '').trim() && !showSuccess && (
            <div className="widget-note-container">
              <div
                className="widget-note-text"
                dangerouslySetInnerHTML={{ __html: normalizeNoteHtml(houseRules) }}
              />
            </div>
          )}

          <ReservationForm
            key={formKey}
            singleEvent
            restaurantId={event.restaurantId}
            restaurantName={restaurant?.name || event.restaurant?.name || ''}
            restaurant={restaurant || event.restaurant || null}
            isWidget={true}
            initialEvent={event}
            onSuccessChange={setShowSuccess}
            onRestart={() => { setShowSuccess(false); setFormKey((k) => k + 1); }}
          />
        </div>
      </div>

      {!showSuccess && (
        <div className="experience-page-actions">
          {/* Restaurant Reservations → return to the restaurant's booking widget. */}
          <button
            type="button"
            className="experience-page-btn ghost"
            onClick={() => navigate(`/widget?restaurantId=${event.restaurantId}`)}
          >
            <i className="bi bi-arrow-left me-1"></i>
            {restaurant?.name ? `${restaurant.name} Reservations` : 'Restaurant Reservations'}
          </button>
          {/* Program → the full experiences listing for this restaurant. */}
          <button
            type="button"
            className="experience-page-btn solid"
            onClick={() => navigate(`/program/${event.restaurantId}`)}
          >
            <i className="bi bi-calendar3 me-1"></i>
            {restaurant?.name ? `${restaurant.name} Program` : 'View Program'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ExperiencePage;
